import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { UserData } from '../types/user';
import { authService, setAuthErrorCallback } from '../services/authService';
import { useError } from './ErrorContext';

interface UserContextType {
  gmalUser: UserData | null;
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  bridgeReady: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [gmalUser, setGmalUser] = useState<UserData | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bridgeReady, setBridgeReady] = useState(false);
  const { showAuthError } = useError();

  const useGmalBridge = process.env.NODE_ENV === 'development' ? false : true; // Set to false for skip gmal bridge loading
  const debugGmalBridge = false;

  const searchBridgeReady = useCallback((bridgeTimeout: NodeJS.Timeout, intervalId: NodeJS.Timeout) => {
    debugGmalBridge && alert('Inizia la ricerca utente dal bridge!!');
    if (mcd !== null && mcd.bridge){
      debugGmalBridge && alert('trovato mcd.bridge!!!!!!!!');
      clearTimeout(bridgeTimeout); // Cancella il timeout se il bridge risponde
      clearInterval(intervalId); // Cancella l'intervallo se il bridge risponde
      setBridgeReady(true);
      const user = mcd.bridge.message('user');
      return new Promise((resolve,reject)=>{
        user.send({ promptlogin: true });
        user.on("data", (data : UserData) => {
          const userDataFormatted = {
            plays: data.plays,
            mcdonaldsId: data.mcdonaldsId,
            deviceId: data.deviceId,
            deviceToken: data.deviceToken,
            email: data.email,
            firstname: data.firstname,
            lastname: data.lastname,
            nickname: data.nickname,
          };
          resolve(userDataFormatted);
        })
        user.on('error', (err : any) => function(){
          // window.location.replace('https://www.mcdonalds.it');
        });
      });
    } else {
      console.log('mcd.bridge non trovato');
    }

  }, [debugGmalBridge]);

  const initializeUser = useCallback(() => {
    if (useGmalBridge) {
      debugGmalBridge && alert('Bridge waiting for ready - in GMAL!');
      // Timeout per controllare se il bridge non risponde
      const bridgeTimeout = setTimeout(() => {
        debugGmalBridge && alert('Bridge timeout - redirecting to McDonalds.it');
        debugGmalBridge && alert(mcd.bridge.message('user'));
      }, 5000); // 5 secondi di timeout
      
      // Ripeti la funzione searchBridgeReady ogni 500ms
      let error_counter = 0;
      const intervalId = setInterval(() => {
        error_counter++;
        if (error_counter > 10) {
          debugGmalBridge && alert('10 errori di bridge, redirecting to McDonalds.it');
          window.location.replace('https://www.mcdonalds.it');
        }
        searchBridgeReady(bridgeTimeout, intervalId)?.then((userDataFormatted: any) => {
          // Aggiorna gmalUser con i dati ottenuti dal bridge
          debugGmalBridge && alert('userDataFormatted ottenuti: ' + userDataFormatted.email);
          setGmalUser(userDataFormatted as UserData);
          if (authService.isAuthenticated()) {
            const userFromJWT = authService.getUserFromJWT();
            if (userFromJWT && userFromJWT.mcdonaldsId === (userDataFormatted?.mcdonaldsId)) {
              debugGmalBridge && alert('JWT data uguali a quelli di gmal');
              setUser(userFromJWT);
            } else {
              debugGmalBridge && alert('JWT data diverse da quelli di gmal');
              localStorage.removeItem('jwt_token');
            }
          }
          setIsLoading(false);
        });

      }, 500);

    } else {
      if (authService.isAuthenticated()) {
        // Estrai i dati utente dal JWT
        console.log('User data from JWT:', authService.getUserFromJWT());
        const userFromJWT = authService.getUserFromJWT();
        if (userFromJWT && userFromJWT.email) {
          console.log('User data from JWT:', userFromJWT);
          setUser(userFromJWT);
        }
      }
      setIsLoading(false);
    }
  }, [useGmalBridge, debugGmalBridge, searchBridgeReady]);

  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  // Registra il callback per gestire errori di autenticazione
  useEffect(() => {
    setAuthErrorCallback((errorData) => {
      showAuthError(errorData);
    });
    
    // Cleanup: rimuovi il callback quando il componente viene smontato
    return () => {
      setAuthErrorCallback(null);
    };
  }, [showAuthError]);

  // Monitora i cambiamenti di gmalUser
  useEffect(() => {
    if (gmalUser) {
      console.log('Gmal user updated:', gmalUser);
    }
  }, [gmalUser]);

  const value: UserContextType = {
    gmalUser,
    user,
    setUser,
    isLoading,
    isAuthenticated: user !== null,
    bridgeReady
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Hook personalizzato per usare il context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 